import React from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/layout/TopBar";
import Footer from "@/components/layout/Footer";
import {
  ArrowRight, Phone, Mail, MapPin, CheckCircle2,
  Target, Heart, ShieldCheck, Zap, Users, Truck, Cpu,
} from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen relative overflow-x-hidden">

      {/* Background — site-wide dark satin backdrop (body bg); no video here. */}

      <div className="relative z-10">
        <TopBar />

        {/* ── HERO ──────────────────────────────────────────── */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl fade-up">
            <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">ABOUT US</p>
            <h1 className="brand-title text-4xl sm:text-5xl md:text-6xl mt-4 leading-tight">
              WHO WE ARE
            </h1>
            <p className="text-white/70 mt-6 text-xl leading-relaxed max-w-2xl">
              DRIVN FREIGHT PTY LTD is Australia's trusted road freight partner since 2022. We built this business with a single promise — to make freight transport reliable, affordable, and effortless for every client we serve.
            </p>
          </div>
        </section>

        {/* ── COMPANY STORY ─────────────────────────────────── */}
        <section className="relative py-24">
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">OUR STORY</p>
                <h2 className="brand-title text-4xl md:text-5xl mt-3">
                  Born in Australia.<br />Built for Reliability.<br />Backed by Technology.
                </h2>
                <p className="text-white/70 mt-5 text-lg leading-relaxed">
                  DRIVN FREIGHT was founded right here in Brisbane, Queensland, with a clear understanding of what businesses need: a freight partner who shows up on time, every time.
                </p>
                <p className="text-white/65 mt-4 text-lg leading-relaxed">
                  We saw too many businesses struggling with unreliable carriers, hidden fees, and poor communication. So, we built DRIVN differently — a company that values and appreciates its drivers & contractor's hard work, where reliability is non-negotiable, and every client is treated as a priority.
                </p>
                <p className="text-white/65 mt-4 text-lg leading-relaxed">
                  Today, we handle everything from local pallet runs to time-critical VIP deliveries, sea freight port pickups, interstate and dedicated permanent hire arrangements for businesses across Brisbane and beyond, with our trusted and reliable transport network.
                </p>
              </div>

              {/* Stat cards — mirroring the Home page HeroStat layout */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Truck,       value: "5,000+",  label: "Deliveries Completed", tint: "#19e6c8" },
                  { icon: ShieldCheck, value: "100%",    label: "On-Time Rate",          tint: "#22c55e" },
                  { icon: Zap,         value: "15,000+", label: "Pallets Moved",         tint: "#22c55e" },
                  { icon: Users,       value: "20+",     label: "Happy Clients",         tint: "#3b82f6" },
                ].map(({ icon: Icon, value, label, tint }) => (
                  <div
                    key={label}
                    className="rounded-2xl p-6 border border-white/10 hover:border-[#22c55e]/50 transition-all group backdrop-blur-md"
                    style={{ background: "rgba(255,255,255,0.03)" }}
                  >
                    <span
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors group-hover:!bg-[#22c55e]/20 group-hover:!text-[#22c55e]"
                      style={{ background: `${tint}22`, color: tint }}
                    >
                      <Icon size={20} />
                    </span>
                    <p className="mt-4 brand-title text-3xl transition-colors duration-300 group-hover:text-[#22c55e]">
                      {value}
                    </p>
                    <p className="text-white/55 text-xs uppercase tracking-widest mt-1 group-hover:text-white/70 transition-colors">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── MISSION & VALUES ──────────────────────────────── */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">OUR FOUNDATION: MISSION AND VALUES</p>
            <h2 className="brand-title text-4xl md:text-5xl mt-3">Mission & Values</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {[
              {
                icon: Target,
                color: "#8a5cff",
                title: "Our Mission",
                desc: "To be Australia's most reliable and affordable road freight partner — providing businesses with a seamless freight experience from booking to delivery, every single time.",
              },
              {
                icon: Heart,
                color: "#19e6c8",
                title: "Our Vision",
                desc: "We envision a freight industry built on trust, transparency, and technology — and we're leading that change. Expand the Drivn family into technology driven areas and build DrivnTeq providing IT services globally and further develop our own e-commerce brands in Australia.",
              },
            ].map(({ icon: Icon, color, title, desc }) => (
              <div
                key={title}
                className="rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all backdrop-blur-md"
                style={{ background: "rgba(255,255,255,0.025)" }}
              >
                <span
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: `${color}18`, color }}
                >
                  <Icon size={26} />
                </span>
                <h3 className="brand-title text-xl">{title}</h3>
                <p className="text-white/60 text-lg mt-3 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* ── TECHNOLOGY ──────────────────────────────────── */}
          <div
            className="rounded-3xl p-6 sm:p-10 md:p-14 border border-white/10 mb-8 backdrop-blur-md"
            style={{ background: "rgba(255,255,255,0.02)" }}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="font-display tracking-[0.4em] text-[#8a5cff] text-sm">DRIVNTEQ</p>
                <h3 className="brand-title text-3xl md:text-4xl mt-3">
                  WHAT DRIVES US: Technology is at the heart of everything we do
                </h3>
                <p className="text-white/65 mt-4 text-lg leading-relaxed">
                  Our founder's background in information technology was the catalyst for building our in-house Transport Management System — developed entirely from scratch. Unlike off-the-shelf solutions, our TMS was purpose-built for the way we operate, giving every stakeholder — clients, drivers, and management — a tailored digital experience that eliminates friction and drives efficiency.
                </p>
                <a
                  href="https://drivnteq.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-[#8a5cff]/30 bg-[#8a5cff]/5 px-5 py-3 hover:border-[#8a5cff]/60 hover:bg-[#8a5cff]/10 transition-all"
                >
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#8a5cff22", color: "#8a5cff" }}>
                    <Cpu size={18} />
                  </span>
                  <div>
                    <p className="brand-title text-sm text-[#8a5cff]">DrivnTeq</p>
                    <p className="text-white/45 text-xs tracking-wider">In-house built TMS platform</p>
                  </div>
                </a>
              </div>

              <div className="space-y-4">
                {[
                  {
                    title: "Dynamic Client & Driver Portals",
                    desc: "Separate, purpose-built portals for clients and drivers with role-based access and real-time data tailored to each user.",
                  },
                  {
                    title: "Seamless Connectivity",
                    desc: "Clients, drivers, and management stay connected through a unified platform with live updates, in-app messaging, and instant notifications.",
                  },
                  {
                    title: "Real-Time Job Status",
                    desc: "Track every job from booking to delivery with live status updates so clients always know exactly where their freight is.",
                  },
                  {
                    title: "Driver Pre-departure Prechecks",
                    desc: "Digital vehicle safety checks completed by drivers before every run — logged, time-stamped, and available on demand.",
                  },
                  {
                    title: "Route Optimisation",
                    desc: "Intelligent routing tools that minimise transit times and fuel costs across every delivery, saving time and money.",
                  },
                  {
                    title: "Sign-on-Glass Proof of Delivery",
                    desc: "Digital POD captured on-device with receiver signature and photo documentation — instant confirmation, zero paperwork.",
                  },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3 group">
                    <span
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-1 transition-colors"
                      style={{ background: "#8a5cff22", color: "#8a5cff" }}
                    >
                      <CheckCircle2 size={16} />
                    </span>
                    <div>
                      <p className="text-white/85 text-lg font-semibold group-hover:text-[#8a5cff] transition-colors">{f.title}</p>
                      <p className="text-white/50 text-lg mt-1 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* What Sets Us Apart */}
          <div
            className="rounded-3xl p-6 sm:p-10 md:p-14 border border-white/10 backdrop-blur-md"
            style={{ background: "rgba(255,255,255,0.02)" }}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">WHAT SETS US APART</p>
                <h3 className="brand-title text-3xl md:text-4xl mt-3">The DRIVN Difference</h3>
                <p className="text-white/65 mt-4 text-lg leading-relaxed">
                  In an industry full of promises, we deliver results. Here's what makes DRIVN FREIGHT the preferred choice for Australian businesses.
                </p>
              </div>
              <div className="space-y-4">
                {[
                  "Dedicated drivers who know your business and your routes",
                  "Real-time tracking and digital proof of delivery",
                  "Flexible service tiers — from affordable standard to urgent VIP",
                  "Direct contact — no call centres, no runarounds",
                  "Transparent pricing with no hidden surcharges",
                  "Digital consignment notes and automated invoicing",
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-[#19e6c8] shrink-0 mt-1" />
                    <p className="text-white/70 text-lg">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── CONTACT & HOURS ───────────────────────────────── */}
        <section className="relative py-24">
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-8">
            <div className="text-center max-w-xl mx-auto mb-14">
              <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">GET IN TOUCH</p>
              <h2 className="brand-title text-4xl md:text-5xl mt-3">Contact Us</h2>
              <p className="text-white/65 mt-4 text-lg">Ready to move your freight? We're here to help.</p>
              <p className="text-white/50 mt-2 text-sm font-display tracking-[0.25em] uppercase">Mon – Fri 9AM – 5PM</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12 max-w-3xl mx-auto">
              {/* Phone */}
              <div className="rounded-xl px-4 py-6 border border-white/10 backdrop-blur-sm hover:border-[#22c55e]/50 transition-all group flex flex-col items-center text-center gap-2" style={{ background: "rgba(255,255,255,0.04)" }}>
                <span className="w-11 h-11 rounded-lg flex items-center justify-center transition-colors group-hover:!bg-[#22c55e]/20 group-hover:!text-[#22c55e]" style={{ background: "#19e6c822", color: "#19e6c8" }}>
                  <Phone size={20} />
                </span>
                <a href="tel:+61470344648" className="brand-title text-lg leading-none group-hover:text-[#22c55e] transition-colors">
                  +61 470 344 648
                </a>
                <p className="text-white/50 text-[11px] uppercase tracking-widest group-hover:text-white/70 transition-colors">Phone</p>
              </div>

              {/* Email */}
              <div className="rounded-xl px-4 py-6 border border-white/10 backdrop-blur-sm hover:border-[#22c55e]/50 transition-all group flex flex-col items-center text-center gap-2" style={{ background: "rgba(255,255,255,0.04)" }}>
                <span className="w-11 h-11 rounded-lg flex items-center justify-center transition-colors group-hover:!bg-[#22c55e]/20 group-hover:!text-[#22c55e]" style={{ background: "#8a5cff22", color: "#8a5cff" }}>
                  <Mail size={20} />
                </span>
                <a
                  href="mailto:hello@drivnfreight.com.au"
                  className="brand-title text-sm leading-tight group-hover:text-[#22c55e] transition-colors break-all"
                >
                  hello@drivnfreight.com.au
                </a>
                <p className="text-white/50 text-[11px] uppercase tracking-widest group-hover:text-white/70 transition-colors">Email</p>
              </div>

              {/* Location */}
              <div className="rounded-xl px-4 py-6 border border-white/10 backdrop-blur-sm hover:border-[#22c55e]/50 transition-all group flex flex-col items-center text-center gap-2" style={{ background: "rgba(255,255,255,0.04)" }}>
                <span className="w-11 h-11 rounded-lg flex items-center justify-center transition-colors group-hover:!bg-[#22c55e]/20 group-hover:!text-[#22c55e]" style={{ background: "#3b82f622", color: "#3b82f6" }}>
                  <MapPin size={20} />
                </span>
                <p className="brand-title text-lg leading-none group-hover:text-[#22c55e] transition-colors">Brisbane, Queensland</p>
                <p className="text-white/50 text-[11px] uppercase tracking-widest group-hover:text-white/70 transition-colors">Location</p>
              </div>
            </div>

            <div className="text-center">
              <Link to="/login" className="pill-btn inline-flex items-center gap-2 !text-base !px-8 !py-4">
                Get Started <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}
